import { useState } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

import { Field, Input, Select } from '../../components/clients/form-primitives';
import { PillButton } from '../../components/clients/PillButton';
import { apiClient } from '../../lib/api-client';
import { useClientsSearch } from '../../hooks/use-clients';
import { useSaasProducts } from '../../hooks/use-saas-products';
import { extractErrorMessage } from '../../lib/errors';

interface FormState {
  client_id: string;
  source_saas_product_id: string;
  external_subscription_id: string;
  external_plan_slug: string;
  external_plan_name: string;
  price: string;
  status: string;
  start_date: string;
}

const initial: FormState = {
  client_id: '',
  source_saas_product_id: '',
  external_subscription_id: '',
  external_plan_slug: '',
  external_plan_name: '',
  price: '',
  status: 'ativa',
  start_date: new Date().toISOString().slice(0, 10),
};

/**
 * Story 8.3 — Importação manual saas_direct.
 * Operador cola dados do SaaS (ID externo + plano + preço) e cria subscription
 * com origin='saas_direct'. Substitui ausência de webhook automático.
 */
export function ImportSaasDirectPage(): JSX.Element {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(initial);
  const [pending, setPending] = useState(false);

  const clients = useClientsSearch({}, 50);
  const saas = useSaasProducts({ limit: 100 });

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPending(true);
    try {
      const res = await apiClient.post<{ data: { id: string; created: boolean } }>(
        '/api/integration/import/saas-direct',
        form,
      );
      toast.success(
        res.data.data.created
          ? 'Subscription saas_direct importada.'
          : 'Subscription já existia — atualizada.',
      );
      navigate(`/subscriptions/${res.data.data.id}`);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Falha ao importar subscription.'));
    } finally {
      setPending(false);
    }
  }

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-5 px-6 py-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-ink">
          Importar assinatura SaaS-direct
        </h1>
        <p className="mt-1 text-sm text-muted">
          Cole os dados do SaaS pra criar subscription com origin=saas_direct. Use enquanto SaaS não
          emitir webhook automático.
        </p>
      </header>

      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-4 rounded-[12px] border border-line bg-panel p-5"
      >
        <Field label="Cliente Owl" required>
          {({ inputId }) => (
            <Select
              id={inputId}
              value={form.client_id}
              onChange={(e) => set('client_id', e.target.value)}
            >
              <option value="" disabled>
                Selecione…
              </option>
              {clients.data?.items.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} {c.document ? `(${c.document})` : ''}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label="SaaS de origem" required>
          {({ inputId }) => (
            <Select
              id={inputId}
              value={form.source_saas_product_id}
              onChange={(e) => set('source_saas_product_id', e.target.value)}
            >
              <option value="" disabled>
                Selecione…
              </option>
              {saas.data?.items.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.slug})
                </option>
              ))}
            </Select>
          )}
        </Field>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label="ID externo da assinatura no SaaS" required>
            {({ inputId }) => (
              <Input
                id={inputId}
                value={form.external_subscription_id}
                onChange={(e) => set('external_subscription_id', e.target.value)}
                placeholder="sub_abc123"
              />
            )}
          </Field>
          <Field label="Preço (R$)" required>
            {({ inputId }) => (
              <Input
                id={inputId}
                type="number"
                step="0.01"
                value={form.price}
                onChange={(e) => set('price', e.target.value)}
                placeholder="99.90"
              />
            )}
          </Field>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label="Slug do plano externo" required>
            {({ inputId }) => (
              <Input
                id={inputId}
                value={form.external_plan_slug}
                onChange={(e) => set('external_plan_slug', e.target.value)}
                placeholder="growth-monthly"
              />
            )}
          </Field>
          <Field label="Nome do plano externo" required>
            {({ inputId }) => (
              <Input
                id={inputId}
                value={form.external_plan_name}
                onChange={(e) => set('external_plan_name', e.target.value)}
                placeholder="Growth Mensal"
              />
            )}
          </Field>
        </div>

        <Field label="Início (snapshot)">
          {({ inputId }) => (
            <Input
              id={inputId}
              type="date"
              value={form.start_date}
              onChange={(e) => set('start_date', e.target.value)}
            />
          )}
        </Field>

        <div className="mt-2 flex justify-end gap-2">
          <PillButton type="button" onClick={() => navigate('/identities')} disabled={pending}>
            Cancelar
          </PillButton>
          <PillButton type="submit" solid disabled={pending}>
            {pending ? 'Importando…' : 'Importar assinatura'}
          </PillButton>
        </div>
      </form>
    </div>
  );
}
